import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchOrganizationAdmins } from '../../../services/organizationService';
import { fetchClubAdmins } from '../../../services/clubService';
import {
  createTeamOwnerAdmin,
  fetchAuctionSettings,
  fetchTournamentAdmins,
  fetchTournamentById,
  saveAuctionSettings,
} from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import {
  getAuctionManagerPath as getCommunityAuctionManagerPath,
  getCommunityId,
} from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath, getClubId } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';

const defaultSettingsSnapshot = {
  auctionDateTime: '',
  pursePerTeam: 0,
  baseBid: 0,
  bidIncrement: 0,
  playersPerTeam: 1,
  numberOfTeams: 1,
  teams: [],
};

const getOwnerIdentifier = (owner) => {
  const adminId =
    owner?.adminId || owner?.admin?.id || owner?.admin?._id || owner?.admin?.id || owner?.admin;
  const raw = adminId || owner?.id || owner?._id || owner?.email;
  return raw?.toString?.() || raw || '';
};

const mapTeamOwners = (owners = []) =>
  owners.map((owner, index) => ({
    id: getOwnerIdentifier(owner) || `owner-${index}`,
    adminId: getOwnerIdentifier(owner),
    name: owner.name || owner.admin?.name || '',
    email: owner.email || owner.admin?.email || '',
    phone: owner.phone || owner.admin?.phone || '',
    teamIndex: Number.isInteger(owner.teamIndex) ? owner.teamIndex : null,
  }));

const normalizeTeams = (teams = [], numberOfTeams = 1) =>
  teams
    .slice(0, numberOfTeams)
    .map((team, index) => ({
      name: team.name || `Team ${index + 1}`,
      logoUrl: team.logoUrl || '',
      captain: team.captain || '',
      retainers: Array.isArray(team.retainers) ? team.retainers : [],
    }));

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const TeamOwnerAdminsPage = () => {
  const { token } = useAuth();
  const {
    auctionManagerId,
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    tournamentId,
  } = useParams();
  const communityId = getCommunityId(routeCommunityId);
  const clubId = getClubId(routeClubId);

  const [teamOwners, setTeamOwners] = useState([]);
  const [organizationAdmins, setOrganizationAdmins] = useState([]);
  const [tournamentAdmins, setTournamentAdmins] = useState([]);
  const [tournamentManagers, setTournamentManagers] = useState([]);
  const [adminLoadError, setAdminLoadError] = useState('');
  const [settingsSnapshot, setSettingsSnapshot] = useState(defaultSettingsSnapshot);
  const [tournamentName, setTournamentName] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingOwner, setIsCreatingOwner] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [ownerForm, setOwnerForm] = useState({
    adminId: '',
    name: '',
    email: '',
    phone: '',
    password: '',
  });

  const auctionManagementPath =
    (communityId
      ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
      : clubId
        ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
        : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId)) || '/management/auction';
  const onlineSetupPath = `${auctionManagementPath}/online`;
  const teamOwnerBiddingPath = `${auctionManagementPath}/online/team-owner-bidding`;

  const availableAdmins = useMemo(() => {
    const adminMap = new Map();

    const mergeManagerRoles = (existingRoles = [], incomingRoles = []) =>
      Array.from(new Set([...(existingRoles || []), ...(incomingRoles || [])]));

    const addAdminToMap = (admin, extraManagerRoles = []) => {
      const adminId = admin?.id || admin?._id;

      if (!adminId) return;

      const existing = adminMap.get(adminId);
      const mergedManagerRoles = mergeManagerRoles(
        existing?.managerRoles,
        mergeManagerRoles(admin?.managerRoles, extraManagerRoles),
      );

      adminMap.set(adminId, {
        ...existing,
        ...admin,
        id: adminId,
        managerRoles: mergedManagerRoles,
      });
    };

    organizationAdmins.forEach((admin) => addAdminToMap(admin));
    tournamentAdmins.forEach((admin) => addAdminToMap(admin));
    tournamentManagers.forEach((admin) => addAdminToMap(admin, admin.managerRoles));

    return Array.from(adminMap.values()).sort((first, second) => {
      const firstLabel = `${first.name || ''} ${first.email || ''}`.toLowerCase();
      const secondLabel = `${second.name || ''} ${second.email || ''}`.toLowerCase();
      return firstLabel.localeCompare(secondLabel);
    });
  }, [organizationAdmins, tournamentAdmins, tournamentManagers]);

  const formatAdminLabel = (admin) => {
    if (!admin) return 'Unknown admin';

    const name = admin.name || admin.email || 'Unknown admin';
    const email = admin.email ? ` (${admin.email})` : '';
    const managerRoles = admin.managerRoles?.length ? ` · ${admin.managerRoles.join(', ')}` : '';

    return `${name}${email}${managerRoles}`;
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (!token || !tournamentId) return;

      setIsLoading(true);
      setErrorMessage('');
      setStatusMessage('');
      setAdminLoadError('');

      try {
        const settingsResponse = await fetchAuctionSettings(tournamentId, token);
        const settings = settingsResponse.settings;
        const owners = mapTeamOwners(settings?.teamOwners || []);
        setTeamOwners(owners);

        if (settings) {
          setTournamentName(settings.tournament?.name || '');
          setSettingsSnapshot({
            auctionDateTime: settings.auctionDateTime || '',
            pursePerTeam: settings.pursePerTeam ?? 0,
            baseBid: settings.baseBid ?? 0,
            bidIncrement: settings.bidIncrement ?? 0,
            playersPerTeam: settings.playersPerTeam || 1,
            numberOfTeams: settings.numberOfTeams || settings.teams?.length || 1,
            teams: normalizeTeams(settings.teams || [], settings.numberOfTeams || settings.teams?.length || 1),
          });
        } else {
          setTournamentName('');
          setSettingsSnapshot(defaultSettingsSnapshot);
        }

        const adminRequests = [
          fetchTournamentAdmins(token, organizationId, { communityId, clubId }),
          fetchTournamentById(tournamentId, token, organizationId, { communityId, clubId }),
        ];
        if (organizationId) {
          adminRequests.push(fetchOrganizationAdmins(token));
        }
        if (clubId) {
          adminRequests.push(fetchClubAdmins(token));
        }

        const [tournamentAdminsResult, tournamentDetailResult, organizationAdminsResult, clubAdminsResult] =
          await Promise.allSettled(adminRequests);

        const adminsFromTournament = [];
        let organizationAdminsResponse = [];
        let clubAdminsResponse = [];
        let adminErrorMessage = '';

        if (tournamentAdminsResult.status === 'fulfilled') {
          adminsFromTournament.push(...(tournamentAdminsResult.value.admins || []));
        } else {
          adminErrorMessage =
            tournamentAdminsResult.reason?.message || 'Unable to load tournament admins right now.';
        }

        if (organizationAdminsResult && organizationAdminsResult.status === 'fulfilled') {
          organizationAdminsResponse = organizationAdminsResult.value.admins || [];
        }
        if (clubAdminsResult && clubAdminsResult.status === 'fulfilled') {
          clubAdminsResponse = clubAdminsResult.value.admins || [];
        }

        if (organizationAdminsResponse.length) {
          const filteredAdmins = organizationId
            ? organizationAdminsResponse.filter((admin) => {
                const adminOrgId = admin.organization?._id || admin.organization?.id || admin.organization;
                return (adminOrgId?.toString?.() || adminOrgId) === organizationId;
              })
            : organizationAdminsResponse;
          setOrganizationAdmins(filteredAdmins);
        } else if (clubAdminsResponse.length) {
          const filteredClubAdmins = clubId
            ? clubAdminsResponse.filter((admin) => {
                const adminClubId = admin.club?._id || admin.club?.id || admin.club;
                return (adminClubId?.toString?.() || adminClubId) === clubId;
              })
            : clubAdminsResponse;
          setOrganizationAdmins(filteredClubAdmins);
        } else {
          setOrganizationAdmins([]);
        }

        if (tournamentDetailResult.status === 'fulfilled') {
          const tournament = tournamentDetailResult.value.tournament;
          const managers = [];

          if (tournament?.registrationManager) {
            managers.push({
              ...tournament.registrationManager,
              managerRoles: ['Player Manager'],
            });
          }

          if (tournament?.auctionManager) {
            managers.push({
              ...tournament.auctionManager,
              managerRoles: ['Auction Manager'],
            });
          }

          if (tournament?.matchManager) {
            managers.push({
              ...tournament.matchManager,
              managerRoles: ['Match Manager'],
            });
          }

          setTournamentManagers(managers);
        } else {
          setTournamentManagers([]);
        }

        setTournamentAdmins(adminsFromTournament);

        if (
          tournamentAdminsResult.status === 'rejected' &&
          (!organizationAdminsResult || organizationAdminsResult.status === 'rejected')
        ) {
          setAdminLoadError(
            adminErrorMessage ||
              'Unable to load admins right now. You can still create a new team owner below.',
          );
        }
      } catch (error) {
        setTeamOwners([]);
        setSettingsSnapshot(defaultSettingsSnapshot);
        setTournamentName('');
        setErrorMessage(error.message || 'Unable to load team owner admins right now.');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [clubId, communityId, organizationId, token, tournamentId]);

  const ownerCount = useMemo(() => teamOwners.length, [teamOwners.length]);
  const teamOptions = useMemo(
    () => normalizeTeams(settingsSnapshot.teams || [], settingsSnapshot.numberOfTeams || 1),
    [settingsSnapshot.numberOfTeams, settingsSnapshot.teams]
  );

  const handleOwnerFieldChange = (ownerId, field, value) => {
    setTeamOwners((previous) =>
      previous.map((owner) => (owner.id === ownerId ? { ...owner, [field]: value } : owner))
    );
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleRemoveOwner = (ownerId) => {
    setTeamOwners((previous) => previous.filter((owner) => owner.id !== ownerId));
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleOwnerTeamChange = (ownerId, value) => {
    const nextValue = value === '' ? null : Number(value);
    setTeamOwners((previous) =>
      previous.map((owner) =>
        owner.id === ownerId
          ? { ...owner, teamIndex: Number.isInteger(nextValue) ? nextValue : null }
          : owner
      )
    );
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleOwnerFormChange = (event) => {
    const { name, value } = event.target;
    setOwnerForm((previous) => ({ ...previous, [name]: value }));
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleAddExistingAdmin = () => {
    if (!ownerForm.adminId) {
      setErrorMessage('Select an existing admin to add as a team owner.');
      return;
    }

    const existingAdmin = availableAdmins.find(
      (admin) => admin._id === ownerForm.adminId || admin.id === ownerForm.adminId
    );
    if (!existingAdmin) {
      setErrorMessage('Unable to find the selected admin.');
      return;
    }

    const alreadyAdded = teamOwners.some((owner) => owner.adminId === ownerForm.adminId);
    if (alreadyAdded) {
      setErrorMessage('This admin is already added as a team owner.');
      return;
    }

    setTeamOwners((previous) => [
      ...previous,
      {
        id: ownerForm.adminId,
        adminId: ownerForm.adminId,
        name: existingAdmin.name || '',
        email: existingAdmin.email || '',
        phone: existingAdmin.phone || existingAdmin.mobile || '',
        teamIndex: null,
      },
    ]);
    setOwnerForm((previous) => ({ ...previous, adminId: '' }));
    setStatusMessage('Existing admin added as team owner. Save changes to persist.');
  };

  const handleCreateTeamOwnerAdmin = async (event) => {
    event?.preventDefault?.();

    if (!ownerForm.name || !ownerForm.email || !ownerForm.password) {
      setErrorMessage('Name, email, and password are required to create a team owner admin.');
      return;
    }

    setIsCreatingOwner(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const payload = {
        name: ownerForm.name,
        email: ownerForm.email,
        password: ownerForm.password,
        organizationId,
      };
      const response = await createTeamOwnerAdmin(tournamentId, payload, token);
      const newAdmin = response.admin || {};

      setOrganizationAdmins((previous) => [newAdmin, ...previous]);
      setTeamOwners((previous) => [
        ...previous,
        {
          id: newAdmin._id || newAdmin.id || newAdmin.email,
          adminId: newAdmin._id || newAdmin.id || '',
          name: newAdmin.name || ownerForm.name,
          email: newAdmin.email || ownerForm.email,
          phone: ownerForm.phone || '',
          teamIndex: null,
        },
      ]);
      setOwnerForm({ adminId: '', name: '', email: '', phone: '', password: '' });
      setStatusMessage('Team owner admin created. Save changes to keep this assignment.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to create a team owner admin right now.');
    } finally {
      setIsCreatingOwner(false);
    }
  };

  const handleSaveOwners = async () => {
    if (!token || !tournamentId) return;

    const normalizedTeamOwners = teamOwners.map((owner, index) => ({
      admin: getOwnerIdentifier(owner),
      name: owner.name?.trim?.() || '',
      email: owner.email?.trim?.() || '',
      phone: owner.phone?.trim?.() || '',
      teamIndex: Number.isInteger(owner.teamIndex) ? owner.teamIndex : undefined,
      id: owner.id || getOwnerIdentifier(owner) || owner.email || `owner-${index}`,
    }));

    if (!settingsSnapshot.playersPerTeam) {
      setErrorMessage('Set players per team in online auction setup before managing team owners.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    setErrorMessage('');

    try {
      const payload = {
        ...settingsSnapshot,
        playersPerTeam: Number(settingsSnapshot.playersPerTeam),
        numberOfTeams: Number(settingsSnapshot.numberOfTeams) || 1,
        teams: normalizeTeams(settingsSnapshot.teams || [], settingsSnapshot.numberOfTeams || 1),
        teamOwners: normalizedTeamOwners,
      };

      const response = await saveAuctionSettings(tournamentId, payload, token);
      const settings = response.settings || payload;
      setTeamOwners(mapTeamOwners(settings.teamOwners || normalizedTeamOwners));
      setSettingsSnapshot((previous) => ({
        ...previous,
        auctionDateTime: settings.auctionDateTime || previous.auctionDateTime,
        pursePerTeam: settings.pursePerTeam ?? previous.pursePerTeam,
        baseBid: settings.baseBid ?? previous.baseBid,
        bidIncrement: settings.bidIncrement ?? previous.bidIncrement,
        playersPerTeam: settings.playersPerTeam || previous.playersPerTeam || 1,
        numberOfTeams: settings.numberOfTeams || previous.numberOfTeams || settings.teams?.length || 1,
        teams: normalizeTeams(settings.teams || previous.teams || [], settings.numberOfTeams || previous.numberOfTeams || 1),
      }));
      setStatusMessage('Team owner admins saved for this tournament.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save team owner admins right now.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4 d-grid gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex flex-column gap-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-primary-subtle text-primary-emphasis">Auction Manager</span>
            {tournamentName ? <span className="text-muted small">{tournamentName}</span> : null}
          </div>
          <h1 className="h4 mb-0">Team owner admins</h1>
          <p className="text-muted mb-0">
            Create, update, or remove team owner admins linked to this tournament&apos;s auction.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Link to={teamOwnerBiddingPath} className="btn btn-outline-secondary">
            Preview team owner bidding screen
          </Link>
          <Link to={onlineSetupPath} className="btn btn-outline-primary">
            Back to online auction setup
          </Link>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-4">
          {isLoading ? (
            <p className="text-muted mb-0">Loading team owner admins…</p>
          ) : (
            <div className="d-grid gap-4">
              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <label className="form-label fw-semibold" htmlFor="existingAdminSelect">
                    Add existing admin
                  </label>
                  <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-end">
                    <select
                      id="existingAdminSelect"
                      name="adminId"
                      className="form-select"
                      value={ownerForm.adminId}
                      onChange={handleOwnerFormChange}
                      disabled={isSaving}
                    >
                      <option value="">Select an organization admin</option>
                      {availableAdmins.map((admin) => (
                        <option key={admin._id || admin.id} value={admin._id || admin.id}>
                          {formatAdminLabel(admin)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddExistingAdmin}
                      disabled={isSaving}
                    >
                      Add
                    </button>
                  </div>
                  {adminLoadError ? <small className="text-danger">{adminLoadError}</small> : null}
                </div>

                <div className="col-12 col-lg-6">
                  <div className="d-grid gap-2">
                    <p className="form-label fw-semibold mb-0">Create team owner admin</p>
                    <div className="row g-2">
                      <div className="col-12 col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={ownerForm.name}
                          onChange={handleOwnerFormChange}
                          placeholder="Name"
                          disabled={isSaving || isCreatingOwner}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={ownerForm.email}
                          onChange={handleOwnerFormChange}
                          placeholder="Email"
                          disabled={isSaving || isCreatingOwner}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <input
                          type="password"
                          className="form-control"
                          name="password"
                          value={ownerForm.password}
                          onChange={handleOwnerFormChange}
                          placeholder="Temp password"
                          disabled={isSaving || isCreatingOwner}
                        />
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-12 col-md-8">
                        <input
                          type="tel"
                          className="form-control"
                          name="phone"
                          value={ownerForm.phone}
                          onChange={handleOwnerFormChange}
                          placeholder="Phone (optional)"
                          disabled={isSaving || isCreatingOwner}
                        />
                      </div>
                      <div className="col-12 col-md-4 d-grid">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleCreateTeamOwnerAdmin}
                          disabled={isSaving || isCreatingOwner}
                        >
                          {isCreatingOwner ? 'Creating…' : 'Create admin'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {errorMessage ? <span className="text-danger small">{errorMessage}</span> : null}
              {statusMessage ? <span className="text-success small">{statusMessage}</span> : null}

              <div className="d-grid gap-3">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div>
                    <p className="form-label fw-semibold mb-0">Team owner admins</p>
                    <small className="text-muted">Update contact details or remove owners below.</small>
                  </div>
                  <span className="badge text-bg-light">{ownerCount} total</span>
                </div>

                {teamOwners.length === 0 ? (
                  <p className="text-muted mb-0">No team owners added yet.</p>
                ) : (
                  <div className="d-grid gap-3">
                    {teamOwners.map((owner) => (
                      <div key={owner.id} className="border rounded p-3 bg-light">
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <div className="d-flex flex-column">
                            <span className="fw-semibold">{owner.name || 'Unnamed owner'}</span>
                            <small className="text-muted">{owner.email || 'No email provided'}</small>
                            {owner.adminId ? (
                              <span className="badge text-bg-white border mt-1 align-self-start">Linked admin</span>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveOwner(owner.id)}
                            disabled={isSaving}
                          >
                            Remove
                          </button>
                        </div>
                        <div className="row g-2 mt-2">
                          <div className="col-12 col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              value={owner.name}
                              onChange={(event) => handleOwnerFieldChange(owner.id, 'name', event.target.value)}
                              placeholder="Owner name"
                              disabled={isSaving}
                            />
                          </div>
                          <div className="col-12 col-md-4">
                            <input
                              type="email"
                              className="form-control"
                              value={owner.email}
                              onChange={(event) => handleOwnerFieldChange(owner.id, 'email', event.target.value)}
                              placeholder="Owner email"
                              disabled={isSaving}
                            />
                          </div>
                          <div className="col-12 col-md-4">
                            <input
                              type="tel"
                              className="form-control"
                              value={owner.phone}
                              onChange={(event) => handleOwnerFieldChange(owner.id, 'phone', event.target.value)}
                              placeholder="Owner phone"
                              disabled={isSaving}
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <label className="form-label text-muted small" htmlFor={`team-owner-${owner.id}`}>
                              Assigned team
                            </label>
                            <select
                              id={`team-owner-${owner.id}`}
                              className="form-select"
                              value={Number.isInteger(owner.teamIndex) ? String(owner.teamIndex) : ''}
                              onChange={(event) => handleOwnerTeamChange(owner.id, event.target.value)}
                              disabled={isSaving || teamOptions.length === 0}
                            >
                              <option value="">Unassigned</option>
                              {teamOptions.map((team, index) => (
                                <option key={`${team.name}-${index}`} value={String(index)}>
                                  {team.name || `Team ${index + 1}`}
                                </option>
                              ))}
                            </select>
                            <small className="text-muted">
                              Match the owner to a specific team so bidders know their franchise.
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="d-flex flex-wrap gap-3 align-items-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveOwners}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving owners…' : 'Save team owner admins'}
                </button>
                <Link to={onlineSetupPath} className="btn btn-outline-secondary">
                  Return to auction setup
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  
    </PageShell>
  );
};

export default TeamOwnerAdminsPage;
